
import React from "react";
import { Puzzle, BarChart2, Calendar } from "lucide-react";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { ToolUsageStats } from "@/hooks/useToolUsageStats";

interface ToolAnalyticsTableProps {
  stats: ToolUsageStats[];
  isLoading: boolean;
}

const getToolDisplayName = (toolName: string): string => {
  switch(toolName) {
    case 'craftGenerator':
      return 'Holiday Elf Craft Generator';
    case 'sceneGenerator':
      return 'Holiday Elf Scene Generator';
    case 'recipeGenerator':
      return 'Holiday Elf Recipe Generator';
    case 'elfNameGenerator':
      return 'Christmas Elf Name Generator';
    default:
      return toolName;
  }
};

const getToolIcon = (toolName: string) => {
  switch(toolName) {
    case 'craftGenerator':
    case 'sceneGenerator':
    case 'recipeGenerator':
    case 'elfNameGenerator':
      return <Puzzle className="h-4 w-4 text-elfGreen" />;
    default:
      return <Puzzle className="h-4 w-4 text-gray-400" />;
  }
};

const ToolAnalyticsTable: React.FC<ToolAnalyticsTableProps> = ({ stats, isLoading }) => {
  if (isLoading) {
    return (
      <div className="h-40 flex items-center justify-center">
        <p>Loading analytics data...</p>
      </div>
    );
  }
  
  if (stats.length === 0) {
    return (
      <div className="h-40 flex items-center justify-center">
        <div className="text-center">
          <BarChart2 className="h-12 w-12 mx-auto text-gray-300 mb-3" />
          <p>No usage data available yet.</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Tool</TableHead>
            <TableHead>Total Usage</TableHead>
            <TableHead>Embedded In</TableHead>
            <TableHead>Last Used</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {stats.map((stat, index) => (
            <TableRow key={index}>
              <TableCell>
                <div className="flex items-center gap-2">
                  {getToolIcon(stat.toolName)}
                  {getToolDisplayName(stat.toolName)}
                </div>
              </TableCell>
              <TableCell>{stat.totalUsage} times</TableCell>
              <TableCell>{stat.postEmbeds} posts</TableCell>
              <TableCell>
                {stat.lastUsed ? (
                  <div className="flex items-center gap-2">
                    <Calendar className="h-3 w-3 text-gray-400" />
                    {new Date(stat.lastUsed).toLocaleDateString()}
                  </div>
                ) : (
                  "Never"
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

export default ToolAnalyticsTable;
